import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { InteractionManager, Platform, ScrollView, View } from 'react-native';

import { isWeb } from 'utils/environment';
import { scrollToTargetInScrollView } from 'utils/scroll/scroll-to-target-in-scroll-view';

const DEFAULT_SCROLL_OFFSET = isWeb ? 0 : 20;
const HEAVY_MODIFIER_THRESHOLD = 20;

interface ScrollCustomizationTargetOptions {
  animated: boolean;
  offset: number;
}

/**
 * In-container scroll for web combo-slot inline customization.
 *
 * Avoids `scrollIntoView({ block: 'start' })`, which pulls targets to the page
 * top and caused upward jumps for bottom-row selections.
 *
 * - When the inline panel top is off-screen (above or below the container), align
 *   its top edge below the container top. This covers row-to-row switches after
 *   scrolling through a long modifier list.
 * - When the top is already visible, only scroll down if the bottom is clipped.
 *   This keeps bottom-row taps from jumping upward.
 */
function scrollCustomizationTarget(
  scrollView: ScrollView,
  target: View,
  { animated, offset }: ScrollCustomizationTargetOptions
): boolean {
  if (!isWeb) {
    scrollToTargetInScrollView(scrollView, target, {
      animated,
      offset,
    });

    return true;
  }

  const targetElement = (target as unknown) as HTMLElement | null;
  const scrollContainer = scrollView.getScrollableNode?.() as HTMLElement | undefined;

  if (!targetElement || !scrollContainer) {
    return false;
  }

  const targetRect = targetElement.getBoundingClientRect();

  if (targetRect.height <= 0) {
    return false;
  }

  const containerRect = scrollContainer.getBoundingClientRect();
  const topInset = containerRect.top + offset;
  const isTopInViewport = targetRect.top >= topInset && targetRect.top <= containerRect.bottom;

  if (!isTopInViewport) {
    const nextScrollTop = scrollContainer.scrollTop + (targetRect.top - topInset);

    scrollContainer.scrollTo({
      top: Math.max(0, nextScrollTop),
      behavior: animated ? 'smooth' : 'auto',
    });

    return true;
  }

  if (targetRect.bottom > containerRect.bottom) {
    const scrollDelta = targetRect.bottom - containerRect.bottom;

    scrollContainer.scrollTo({
      top: scrollContainer.scrollTop + scrollDelta,
      behavior: animated ? 'smooth' : 'auto',
    });
  }

  return true;
}

interface UseScrollToCustomizationOptions {
  /** Ref to the parent `ScrollView` that should scroll to the inline panel. */
  scrollViewRef: React.RefObject<ScrollView | null>;
  /**
   * Value that changes when the user selects a different combo-slot item.
   * Auto-scroll runs when this identity changes after the hook is enabled; the
   * initial value is ignored so opening the screen does not scroll.
   */
  scrollTrigger: unknown;
  /** When false, auto-scroll and deferred Android gates are inactive. */
  enabled?: boolean;
  /**
   * True when the combo slot picker shows more than one option.
   * On Android, inline customization visibility is deferred until interactions
   * finish, or until the consumer calls `onUserComboSlotSelection` on user tap.
   */
  hasMultipleComboSlotOptions?: boolean;
  /**
   * Number of modifiers for the currently selected combo-slot item.
   * On Android, mounting 3+ modifiers is deferred until after interactions,
   * and scroll animation is disabled at this threshold.
   */
  modifierCount?: number;
  /** Pixels above the customization panel after scroll. Defaults to 70. */
  offset?: number;
  /**
   * Whether `scrollTo` is animated. Defaults to false on Android when
   * `modifierCount` is 3 or more; otherwise true.
   */
  animated?: boolean;
}

interface UseScrollToCustomizationResult {
  /** Attach to the wrapper `View` around the inline customization panel. */
  customizationRef: React.RefObject<View | null>;
  /**
   * Pass to that wrapper's `onLayout`. Scrolls once layout is measured when a
   * scroll was scheduled and the Android multi-slot deferral is not active.
   */
  handleCustomizationLayout: () => void;
  /** Imperatively scroll to `customizationRef`. Returns false when refs are not ready. */
  scrollToCustomization: () => boolean;
  /**
   * Gate for inserting the inline customization shell into the combo-slot grid.
   * False on Android multi-slot flows until interactions complete or the user
   * selects a slot and the consumer calls `onUserComboSlotSelection`.
   */
  canShowInlineCustomization: boolean;
  /**
   * Gate for mounting the modifier tree inside the inline panel. False on
   * Android when `modifierCount` is 3+ until interactions complete.
   */
  canMountCustomizationContent: boolean;
  /**
   * Call from the combo-slot press handler on Android multi-slot flows to reveal
   * inline customization immediately and schedule a scroll.
   */
  onUserComboSlotSelection: () => void;
}

/**
 * Scroll and mount orchestration for inline product customization in the static
 * combo-slot grid layout.
 *
 * Consumers wire the returned ref and layout handler onto a wrapper `View` placed
 * below the selected combo-slot row, and gate modifier rendering with
 * `canShowInlineCustomization` / `canMountCustomizationContent`.
 *
 * Auto-scroll runs when `scrollTrigger` changes while `enabled` is true and both
 * deferral gates allow it. Scroll is scheduled after interactions and the next
 * animation frame so layout can settle before measuring.
 *
 * Android-specific deferrals reduce jank when the grid splits to show inline
 * customization or when mounting large modifier trees:
 * - Multi-slot picker: defer showing the inline panel until interactions finish.
 * - 3+ modifiers: defer mounting modifier content; remount when `scrollTrigger`
 *   changes; use non-animated scroll by default.
 *
 * On web, scroll stays inside the parent `ScrollView`. It aligns the inline panel
 * top when that top is off-screen (for example after switching combo-slot rows),
 * and only scrolls down when the top is already visible but the bottom is clipped.
 *
 * @returns Refs, layout/scroll callbacks, Android deferral gates, and the combo-slot
 * selection handler used by static-layout customization screens.
 */
export function useScrollToCustomization({
  scrollViewRef,
  scrollTrigger,
  enabled = true,
  hasMultipleComboSlotOptions = false,
  modifierCount = 0,
  animated: animatedOption,
  offset = DEFAULT_SCROLL_OFFSET,
}: UseScrollToCustomizationOptions): UseScrollToCustomizationResult {
  const isAndroidMultipleComboSlotOptions =
    Platform.OS === 'android' && hasMultipleComboSlotOptions;
  const isAndroidHeavyModifiers =
    Platform.OS === 'android' && modifierCount >= HEAVY_MODIFIER_THRESHOLD;

  const animated =
    animatedOption ?? (Platform.OS !== 'android' || modifierCount < HEAVY_MODIFIER_THRESHOLD);

  const [canShowInlineCustomization, setCanShowInlineCustomization] = useState(
    !isAndroidMultipleComboSlotOptions
  );
  const [canMountCustomizationContent, setCanMountCustomizationContent] = useState(
    !isAndroidHeavyModifiers
  );

  const customizationRef = useRef<View>(null);
  const pendingScrollToCustomizationRef = useRef(false);
  const previousScrollTriggerRef = useRef<unknown>(undefined);
  const wasEnabledRef = useRef(false);

  useEffect(() => {
    if (!isAndroidMultipleComboSlotOptions) {
      return;
    }

    const task = InteractionManager.runAfterInteractions(() => {
      setCanShowInlineCustomization(true);
    });

    return () => task.cancel?.();
  }, [isAndroidMultipleComboSlotOptions]);

  useEffect(() => {
    if (!isAndroidHeavyModifiers) {
      setCanMountCustomizationContent(true);
      return;
    }

    setCanMountCustomizationContent(false);

    const task = InteractionManager.runAfterInteractions(() => {
      setCanMountCustomizationContent(true);
    });

    return () => task.cancel?.();
  }, [isAndroidHeavyModifiers, scrollTrigger]);

  const scrollToCustomization = useCallback(() => {
    const scrollView = scrollViewRef.current;
    const target = customizationRef.current;

    if (!scrollView || !target) {
      return false;
    }

    return scrollCustomizationTarget(scrollView, target, {
      animated,
      offset,
    });
  }, [animated, offset, scrollViewRef]);

  const tryCompletePendingScroll = useCallback(() => {
    if (!pendingScrollToCustomizationRef.current) {
      return;
    }

    if (scrollToCustomization()) {
      pendingScrollToCustomizationRef.current = false;
    }
  }, [scrollToCustomization]);

  const scheduleScrollToCustomization = useCallback(() => {
    pendingScrollToCustomizationRef.current = true;

    if (!isWeb) {
      const task = InteractionManager.runAfterInteractions(() => {
        requestAnimationFrame(() => {
          tryCompletePendingScroll();
        });
      });

      return () => {
        task.cancel?.();
      };
    }

    const task = InteractionManager.runAfterInteractions(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          tryCompletePendingScroll();
        });
      });
    });

    return () => {
      task.cancel?.();
    };
  }, [tryCompletePendingScroll]);

  const handleCustomizationLayout = useCallback(() => {
    if (!pendingScrollToCustomizationRef.current) {
      return;
    }

    if (isAndroidMultipleComboSlotOptions && !isWeb) {
      return;
    }

    requestAnimationFrame(() => {
      tryCompletePendingScroll();
    });
  }, [isAndroidMultipleComboSlotOptions, tryCompletePendingScroll]);

  const shouldAutoScroll = enabled && canShowInlineCustomization && canMountCustomizationContent;

  useLayoutEffect(() => {
    if (!shouldAutoScroll) {
      wasEnabledRef.current = false;
      return;
    }

    if (!wasEnabledRef.current) {
      wasEnabledRef.current = true;
      previousScrollTriggerRef.current = scrollTrigger;
      return;
    }

    if (Object.is(previousScrollTriggerRef.current, scrollTrigger)) {
      return;
    }

    previousScrollTriggerRef.current = scrollTrigger;

    return scheduleScrollToCustomization();
  }, [shouldAutoScroll, scrollTrigger, scheduleScrollToCustomization]);

  useEffect(() => {
    if (
      !enabled ||
      !canShowInlineCustomization ||
      !canMountCustomizationContent ||
      !pendingScrollToCustomizationRef.current
    ) {
      return;
    }

    return scheduleScrollToCustomization();
  }, [
    canMountCustomizationContent,
    canShowInlineCustomization,
    enabled,
    scheduleScrollToCustomization,
  ]);

  const onUserComboSlotSelection = useCallback(() => {
    if (!isAndroidMultipleComboSlotOptions) {
      return;
    }

    setCanShowInlineCustomization(true);
    scheduleScrollToCustomization();
  }, [isAndroidMultipleComboSlotOptions, scheduleScrollToCustomization]);

  return {
    customizationRef,
    handleCustomizationLayout,
    scrollToCustomization,
    canShowInlineCustomization,
    canMountCustomizationContent,
    onUserComboSlotSelection,
  };
}
